/**
 * `/settings/profession` — **Profession & website conduct** (w12b, C11,
 * FR-A8 §9, T-1.21; ADR-0014).
 *
 * The owner, 2026-09-25: *"profession can be manage in setting along with
 * Website conduct"*. Until Wave 12 the profession was asked once, in
 * onboarding, and never again; the conduct switches it defaults were drawn
 * twice, in Settings and on `/site/policy`.
 *
 * Two sections on one page, because one decides the other:
 *
 * 1. **The profession**, as the four illustrated cards onboarding uses.
 *    Choosing a different one does not save — it opens a dialog that says
 *    **what moves** (the conduct defaults, the profession line under the
 *    firm's name, the templates suggested first) and **what stays** (the
 *    firm's own switch overrides, its chosen template, its data), and the
 *    dialog's confirm is the save. The request is `PATCH /firm/profile` with
 *    `{profession}` and nothing else (I-1: *"`profession` stays patchable at
 *    any time"*), so nothing the dialog promises stays can be touched by it.
 *    **w13b** (ADR-0031): a fifth card, *Other*. Moving to it asks *"What
 *    do you do?"* inside the dialog — the profession and the firm's words go
 *    in one request, because the service refuses `other` without them — and
 *    while the firm is `other` the words are editable under the cards, saved
 *    by the page's edit bar (Wave 14E, T-4.67; ADR-0032 §5). The bar is always
 *    drawn; for a regulated firm it has nothing to save, because a new
 *    profession is saved by its dialog's confirm and nowhere else.
 * 2. **The conduct switches** were drawn here too until Wave 14. They exist
 *    once now, on *Your website → What you may say* (C13, ADR-0032 §3), and
 *    this page links there. The policy is still **read** here, for one
 *    number: how many switches the firm changed, which the dialog promises
 *    will stay — and re-read after a change, because the defaults have moved.
 */

import { Alert, ConfirmDialog, Skeleton } from '@fiveox/ui'
import { ArrowRight, Scale, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'

import { overrideCount } from '../../../components/ConductSwitches'
import { EditBar, useLeaveGuard } from '../../../components/edit'
import { HubRow } from '../../../components/HubCard'
import { SETTINGS, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { type FirmProfile, type Profession, firm } from '../../../lib/api/firm'
import { site } from '../../../lib/api/site'
import {
  OtherLabelField,
  ProfessionCards,
  professionMeta,
  professionName,
} from '../onboarding/ProfessionCards'
import { SettingsPage, SettingsSection } from './SettingsPage'

/** The dialog's two lists, as words — pure, so the promise is testable. */
export function professionChange({
  from,
  to,
  overrides,
  templateName,
  fromLabel = null,
  toLabel = null,
}: {
  from: Profession | null
  to: Profession
  overrides: number
  templateName: string | null
  /** w13b — the firm's words for `other`, before and after (ADR-0031). */
  fromLabel?: string | null
  toLabel?: string | null
}): { moves: string[]; stays: string[] } {
  const next = professionMeta(to)!
  const nextName = professionName(to, toLabel)!
  const beforeName = from ? professionName(from, fromLabel) : null
  return {
    moves: [
      to === 'other'
        ? 'Your website conduct defaults — no conduct policy applies to your practice, so every switch you have not changed is allowed, nothing is cited and no disclaimer is drawn.'
        : `Your website conduct defaults — every switch you have not changed starts from ${next.institute}’s guidelines.`,
      beforeName
        ? `The line under your firm’s name on your website — “${beforeName}” becomes “${nextName}”, unless you have written your own.`
        : `The line under your firm’s name on your website reads “${nextName}”, unless you have written your own.`,
      to === 'other'
        ? 'The templates suggested first in the gallery — the ones that suit any practice.'
        : `The templates suggested first in the gallery — those drawn for ${next.plural}.`,
    ],
    stays: [
      overrides === 0
        ? 'Your own conduct choices — you have not changed any switch, so there is nothing to keep.'
        : `Your own conduct choices — the ${overrides === 1 ? 'switch' : `${overrides} switches`} you changed stay${overrides === 1 ? 's' : ''} as you set ${overrides === 1 ? 'it' : 'them'}.`,
      templateName
        ? `Your website’s template — ${templateName} stays until you choose another.`
        : 'Your website’s template — whichever you choose stays yours.',
      'Your clients, cases, services and invoices, and every page you have written. Nothing is moved or deleted.',
    ],
  }
}

export function ProfessionSettings() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')

  const [profile, setProfile] = useState<Partial<FirmProfile> | null>(null)
  const [templateName, setTemplateName] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<number | null>(null)
  const [pending, setPending] = useState<Profession | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [saved, setSaved] = useState<Profession | null>(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    firm
      .read()
      .then((body) => {
        setProfile(body.data.profile)
        const code = body.data.profile.website_template
        if (!code) return
        setTemplateName(code)
        // The name is a courtesy; the code stands in if the list is not ours
        // to read (`site:can_view`).
        site
          .templates()
          .then((list) =>
            setTemplateName(list.data.templates.find((t) => t.code === code)?.name ?? code),
          )
          .catch(() => undefined)
      })
      .catch(() => setError('Your profession could not be loaded.'))
  }, [])

  // How many switches the firm changed — the dialog's *what stays*. Re-read
  // after a profession change (`version`), because every default moved.
  useEffect(() => {
    let cancelled = false
    firm
      .policy()
      .then((body) => !cancelled && setOverrides(overrideCount(body.data)))
      .catch(() => !cancelled && setOverrides(null))
    return () => {
      cancelled = true
    }
  }, [version])

  const current = (profile?.profession ?? null) as Profession | null
  const currentLabel = profile?.profession_label ?? null

  // w13b — ADR-0031: *"What do you do?"*. Asked in the dialog when the firm
  // moves to `other` (the change and its words are one request, or the
  // service refuses it), and editable under the cards while it is `other`.
  const [pendingLabel, setPendingLabel] = useState('')
  const [labelDraft, setLabelDraft] = useState<string | null>(null)
  const [labelError, setLabelError] = useState<string | null>(null)
  const shownLabel = labelDraft ?? currentLabel ?? ''
  const labelDirty = labelDraft !== null && labelDraft.trim() !== (currentLabel ?? '')
  const labelProblem = labelDirty && !labelDraft?.trim() ? 'Tell us what your firm does — for example, Architects.' : null
  const guard = useLeaveGuard(labelDirty)

  const confirm = async () => {
    if (!pending) return
    if (pending === 'other' && !pendingLabel.trim()) {
      setDialogError('Tell us what your firm does — for example, Architects.')
      return
    }
    setSaving(true)
    setDialogError(null)
    try {
      const body = await firm.updateProfile(
        pending === 'other'
          ? { profession: pending, profession_label: pendingLabel.trim() }
          : { profession: pending },
      )
      setProfile(body.data)
      setSaved(pending)
      setPending(null)
      setLabelDraft(null)
      setVersion((n) => n + 1)
    } catch (err) {
      setDialogError(err instanceof ApiError ? err.message : 'The profession could not be changed.')
    } finally {
      setSaving(false)
    }
  }

  const saveLabel = async () => {
    if (!labelDraft?.trim()) {
      setLabelError('Tell us what your firm does — for example, Architects.')
      return
    }
    setSaving(true)
    setLabelError(null)
    try {
      const body = await firm.updateProfile({ profession_label: labelDraft.trim() })
      setProfile(body.data)
      setLabelDraft(null)
    } catch (err) {
      setLabelError(err instanceof ApiError ? err.message : 'Your words could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const change =
    pending &&
    professionChange({
      from: current,
      to: pending,
      overrides: overrides ?? 0,
      templateName,
      fromLabel: currentLabel,
      toLabel: pendingLabel.trim() || null,
    })

  return (
    <SettingsPage
      title="Profession"
      description="What your practice is. It sets your website’s conduct defaults, the line under your firm’s name and the templates suggested first."
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}
      {saved && (
        <Alert tone="success">
          Your firm is now recorded as {professionName(saved, currentLabel)}. Your website’s conduct
          defaults have moved with it; your own choices have not.
        </Alert>
      )}

      <SettingsSection
        id="profession"
        icon={<Scale size={18} aria-hidden />}
        title="Your profession"
        description="It sets your conduct defaults, the line under your firm’s name and the templates suggested first."
      >
        {!profile ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <ProfessionCards
              value={current}
              disabled={!editable || saving}
              onChange={(next) => {
                if (next === current) return
                setDialogError(null)
                setSaved(null)
                setPendingLabel(currentLabel ?? '')
                setPending(next)
              }}
            />
            {current === 'other' && (
              <div
                className="space-y-3 rounded-2xl border border-primary/40 bg-primary/5 p-4"
                data-other-label-editor=""
              >
                <OtherLabelField
                  value={shownLabel}
                  onChange={(next) => {
                    setLabelError(null)
                    setLabelDraft(next)
                  }}
                  disabled={!editable || saving}
                  error={labelError}
                />
                <p className="text-muted-foreground text-xs">
                  Your website’s line under your firm’s name follows these words, unless you wrote
                  your own in Navigation.
                </p>
              </div>
            )}
          </div>
        )}
      </SettingsSection>

      {/* The conduct switches live once, under the website (C13, ADR-0032 §3). */}
      <HubRow
        rowKey="conduct"
        href="/site/policy"
        icon={ShieldCheck}
        title="What you may say — your website’s conduct rules"
        description={
          current === 'other'
            ? 'No conduct policy applies to your practice, so each switch is yours to set.'
            : overrides
              ? `Five switches, each starting from your institute’s guidelines — you have changed ${overrides === 1 ? 'one' : overrides}.`
              : 'Five switches, each starting from your institute’s guidelines, with the clause it comes from.'
        }
      />

      <EditBar
        dirty={labelDirty}
        saving={saving && !pending}
        problem={labelProblem}
        editable={editable}
        onSave={() => void saveLabel()}
        onDiscard={() => {
          setLabelError(null)
          setLabelDraft(null)
        }}
      />

      <ConfirmDialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        title={pending ? `Change to ${professionMeta(pending)?.title}?` : 'Change profession'}
        confirmLabel="Change profession"
        cancelLabel={current ? `Keep ${professionMeta(current)?.title}` : 'Not now'}
        onConfirm={() => void confirm()}
        busy={saving}
        busyLabel="Changing…"
        error={dialogError}
        width="lg"
      >
        {change && (
          <div className="space-y-4 text-sm" data-profession-change="">
            {pending === 'other' && (
              <OtherLabelField
                value={pendingLabel}
                onChange={(next) => {
                  setDialogError(null)
                  setPendingLabel(next)
                }}
                disabled={saving}
              />
            )}
            <div>
              <p className="flex items-center gap-2 font-semibold text-foreground">
                <ArrowRight size={16} className="text-primary" aria-hidden /> What moves
              </p>
              <ul className="mt-2 space-y-1.5 pl-6 text-muted-foreground" data-list="moves">
                {change.moves.map((line) => (
                  <li key={line} className="list-disc">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="flex items-center gap-2 font-semibold text-foreground">
                <ShieldCheck size={16} className="text-primary" aria-hidden /> What stays
              </p>
              <ul className="mt-2 space-y-1.5 pl-6 text-muted-foreground" data-list="stays">
                {change.stays.map((line) => (
                  <li key={line} className="list-disc">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </ConfirmDialog>
    </SettingsPage>
  )
}
